import { useEffect, useRef, useCallback, useState } from 'react';
import { useLogout } from '@refinedev/core';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../lib/axios';
import { authService } from '../services/auth.service';

interface UseAuthValidationOptions {
  /** Validation interval in milliseconds (default: 60000 = 1 minute) */
  interval?: number;
  /** Whether the hook is enabled (default: true) */
  enabled?: boolean;
}

/**
 * Hook for periodic authentication validation
 *
 * Features:
 * - Calls /api/auth/validate every 1 minute
 * - Shows expired dialog when token is invalid
 * - Auto-redirects to login after 5 seconds countdown
 */
export const useAuthValidation = (options: UseAuthValidationOptions = {}) => {
  const { interval = 60000, enabled = true } = options;

  const { mutate: logout } = useLogout();
  const navigate = useNavigate();

  const [isExpired, setIsExpired] = useState(false);
  const [countdown, setCountdown] = useState(5);

  // All refs to avoid dependency issues
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isValidatingRef = useRef(false);
  const isSetupRef = useRef(false);
  const logoutRef = useRef(logout);
  const navigateRef = useRef(navigate);
  const intervalValueRef = useRef(interval);
  const enabledRef = useRef(enabled);

  // Update refs when values change
  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    intervalValueRef.current = interval;
  }, [interval]);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  const handleConfirm = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    setIsExpired(false);
    logoutRef.current();
    navigateRef.current('/login', { replace: true });
  }, []);

  // Single setup effect - runs only once on mount
  useEffect(() => {
    // Prevent double setup in strict mode
    if (isSetupRef.current) {
      return;
    }

    const clearAllIntervals = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };

    const handleExpired = () => {
      clearAllIntervals();
      setIsExpired(true);
      setCountdown(5);

      // Start 5-second countdown
      countdownRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            if (countdownRef.current) {
              clearInterval(countdownRef.current);
              countdownRef.current = null;
            }
            logoutRef.current();
            navigateRef.current('/login', { replace: true });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    };

    const validateAuth = async () => {
      // Check if enabled
      if (!enabledRef.current) {
        return;
      }

      // Prevent concurrent validations
      if (isValidatingRef.current) {
        return;
      }

      isValidatingRef.current = true;

      try {
        const { accessToken } = authService.getStoredTokens();

        if (!accessToken) {
          console.warn('[AuthValidation] No token found');
          handleExpired();
          return;
        }

        const response = await axiosInstance.get('/auth/validate', {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (!response.data.success) {
          console.warn('[AuthValidation] Token validation failed');
          handleExpired();
        }
      } catch (error: unknown) {
        console.error('[AuthValidation] Validation error:', error);

        if (
          error &&
          typeof error === 'object' &&
          'response' in error &&
          (error as { response?: { status?: number } }).response?.status === 401
        ) {
          handleExpired();
        }
      } finally {
        isValidatingRef.current = false;
      }
    };

    // Check if user has token before starting
    const { accessToken } = authService.getStoredTokens();
    if (!accessToken) {
      return;
    }

    isSetupRef.current = true;

    // Initial validation after short delay
    const initialTimeout = setTimeout(() => {
      validateAuth();
    }, 1000);

    // Set up periodic validation
    intervalRef.current = setInterval(() => {
      validateAuth();
    }, intervalValueRef.current);

    return () => {
      isSetupRef.current = false;
      clearTimeout(initialTimeout);
      clearAllIntervals();
    };
  }, []); // Empty dependency array - runs only once

  return {
    isExpired,
    countdown,
    handleConfirm,
  };
};
